import { debugLog, debugWarn } from './logger';

/**
 * Configuración de acceso a citas
 * TODO: Estos valores se podrán configurar desde el panel de admin
 */
const ACCESS_CONFIG = {
  preAccessMinutes: 5,      // Minutos antes de la cita para habilitar acceso
  postAccessMinutes: 60,    // Minutos después de la cita para mantener acceso
  requirePayment: true      // Si se requiere pago para acceder
};

/**
 * Verifica si un usuario puede acceder a una cita (chat/video)
 *
 * FIX: El objeto `appointment` que devuelve la API NO incluye el campo `payment`
 * embebido (los pagos viven en una colección separada y se consultan aparte
 * con paymentService.getPaymentStatus). Por eso appointment?.payment?.status
 * siempre era undefined y el check de pago bloqueaba el acceso aunque el pago
 * estuviera confirmado.
 *
 * Solución: se acepta `externalPaymentStatus` como segundo argumento opcional.
 * El componente que ya tiene el estado de pago (DashboardPatient) lo pasa
 * directamente. Si no se provee, se intenta leer del objeto appointment como
 * fallback para no romper otros posibles usos.
 *
 * @param {Object} appointment          - Objeto de cita con scheduledDate
 * @param {string} userRole             - 'patient' o 'doctor'
 * @param {string|null} externalPaymentStatus - Estado de pago externo ('completed', 'pending', etc.)
 * @returns {Object} { canAccess, reason, remainingTime, windowStart, windowEnd, expired }
 */
export const checkAppointmentAccess = (appointment, userRole = 'patient', externalPaymentStatus = null) => {
  debugLog('\n🔐 [checkAppointmentAccess] Verificando acceso');
  debugLog('📊 Appointment ID:', appointment?._id);
  debugLog('👤 User Role:', userRole);
  debugLog('📅 Scheduled Date:', appointment?.scheduledDate);
  debugLog('💳 External Payment Status:', externalPaymentStatus);
  debugLog('💳 Appointment Payment Status (fallback):', appointment?.payment?.status);

  // 👨‍⚕️ Doctores siempre tienen acceso
  if (userRole === 'doctor') {
    debugLog('✅ Doctor tiene acceso ilimitado');
    return {
      canAccess: true,
      reason: 'Doctor tiene acceso completo',
      remainingTime: null,
      windowStart: null,
      windowEnd: null,
      expired: false
    };
  }

  // 💳 Verificar pago si es requerido
  if (ACCESS_CONFIG.requirePayment) {
    // Los dos orígenes hablan vocabularios distintos y eso hacía inútil el
    // fallback: getPaymentStatus (colección Payment) devuelve 'completed',
    // mientras que appointment.payment.status es un enum aparte
    // ('pending' | 'paid' | 'failed' | 'refunded') que NUNCA puede valer
    // 'completed'. Una cita pagada que cayera al fallback se bloqueaba siempre.
    // Se normalizan ambos a un único concepto: pagada o no.
    const rawStatus = externalPaymentStatus ?? appointment?.payment?.status;
    const paymentStatus = rawStatus === 'paid' ? 'completed' : rawStatus;
    debugLog('💵 Verificando pago, estado resuelto:', paymentStatus, '(origen:', rawStatus, ')');

    if (!paymentStatus || paymentStatus !== 'completed') {
      debugWarn('⚠️ Pago no completado');
      return {
        canAccess: false,
        reason: 'payment_required',
        message: 'Debes completar el pago para acceder a la consulta',
        remainingTime: null,
        windowStart: null,
        windowEnd: null,
        expired: false
      };
    }
    debugLog('✅ Pago completado');
  }

  // 🕐 Verificar ventana de tiempo
  const now = new Date();
  const scheduledDate = new Date(appointment.scheduledDate);

  // Calcular ventana de acceso
  const windowStart = new Date(scheduledDate.getTime() - ACCESS_CONFIG.preAccessMinutes * 60000);
  const windowEnd = new Date(scheduledDate.getTime() + ACCESS_CONFIG.postAccessMinutes * 60000);

  debugLog('⏰ Ahora:', now.toISOString());
  debugLog('📅 Cita programada:', scheduledDate.toISOString());
  debugLog('🟢 Ventana inicio:', windowStart.toISOString());
  debugLog('🔴 Ventana fin:', windowEnd.toISOString());

  // Verificar si está antes de la ventana
  if (now < windowStart) {
    const minutesUntilStart = Math.ceil((windowStart - now) / 60000);
    debugLog(`⏳ Falta ${minutesUntilStart} minutos para acceder`);

    return {
      canAccess: false,
      reason: 'too_early',
      message: `Podrás acceder ${ACCESS_CONFIG.preAccessMinutes} minutos antes de la cita`,
      remainingTime: minutesUntilStart,
      windowStart: windowStart.toISOString(),
      windowEnd: windowEnd.toISOString(),
      expired: false
    };
  }

  // Verificar si está después de la ventana
  if (now > windowEnd) {
    debugLog('❌ Ventana de acceso expirada');

    return {
      canAccess: false,
      reason: 'expired',
      message: 'La ventana de acceso a esta consulta ha expirado',
      remainingTime: null,
      windowStart: windowStart.toISOString(),
      windowEnd: windowEnd.toISOString(),
      expired: true
    };
  }

  // ✅ Está dentro de la ventana
  const minutesRemaining = Math.floor((windowEnd - now) / 60000);
  debugLog(`✅ Acceso permitido. Tiempo restante: ${minutesRemaining} minutos`);

  return {
    canAccess: true,
    reason: 'within_window',
    message: 'Acceso permitido',
    remainingTime: minutesRemaining,
    windowStart: windowStart.toISOString(),
    windowEnd: windowEnd.toISOString(),
    expired: false
  };
};

/**
 * Formatea el mensaje de acceso para mostrar al usuario
 */
export const formatAccessMessage = (accessResult) => {
  if (!accessResult) return 'Verificando acceso...';

  switch (accessResult.reason) {
    case 'payment_required':
      return '💳 Debes completar el pago para acceder';

    case 'too_early':
      return `⏳ Disponible en ${accessResult.remainingTime} minutos`;

    case 'expired':
      return '⏰ La ventana de acceso ha expirado';

    case 'within_window':
      if (accessResult.remainingTime) {
        return `✅ Disponible (${accessResult.remainingTime} min restantes)`;
      }
      return '✅ Disponible ahora';

    default:
      return accessResult.message || 'Acceso no disponible';
  }
};

/**
 * Retorna el badge/clase CSS según el estado de acceso
 */
export const getAccessBadge = (accessResult) => {
  if (!accessResult) return 'bg-gray-100 text-gray-800';

  if (accessResult.canAccess) {
    if (accessResult.remainingTime && accessResult.remainingTime < 10) {
      return 'bg-orange-100 text-orange-800';
    }
    return 'bg-green-100 text-green-800';
  }

  if (accessResult.expired) {
    return 'bg-gray-100 text-gray-800';
  }

  if (accessResult.reason === 'too_early') {
    return 'bg-yellow-100 text-yellow-800';
  }

  return 'bg-red-100 text-red-800';
};

/**
 * Exportar configuración para usar en admin panel (futuro)
 */
export const getAccessConfig = () => ({ ...ACCESS_CONFIG });

/**
 * Actualizar configuración (futuro - desde admin panel)
 */
export const updateAccessConfig = (newConfig) => {
  debugLog('🔧 [updateAccessConfig] Actualizando configuración:', newConfig);
  Object.assign(ACCESS_CONFIG, newConfig);
};
